import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { BarChart2, AlertTriangle, List, PieChart, TrendingUp, BookOpen, Clock, RefreshCw, Info, Trophy, Dice5, Zap, Search, ArrowRight } from "lucide-react";
import axios from "axios";
import DOMPurify from 'dompurify';
import { useTranslation } from "react-i18next";
import AnnouncementPopup from "../../AnnouncementPopup/AnnouncementPopup";
import styles from "./style/NavPage.module.css";
import commonStyles from "../../../styles/Common.module.css";

const API_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_PROFILE = process.env.REACT_APP_DEFAULT_PROFILE_IMAGE;

const SORT_MAP = { return: "ret_1m", sharpe: "sharpe_1m", mdd: "mdd_1m", volatility: "vol_1m" };
const SORT_ORDER = ["return", "sharpe", "volatility", "mdd"];

// 캐시 설정
const CACHE_KEY = "navPageData_v1";
const CACHE_DURATION = 5 * 60 * 1000; // 5분

const sanitizeData = (obj, keys) => {
  const newObj = { ...obj };
  keys.forEach(key => { if(newObj[key]) newObj[key] = DOMPurify.sanitize(newObj[key], { USE_PROFILES: { html: false } })});
  return newObj;
};

function NavPage() {
  const { t } = useTranslation();
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [data, setData] = useState({ portfolios: {}, posts: [], sortIdx: 0, portIdx: 0 });
  const [hover, setHover] = useState({ portfolio: false });
  const [loading, setLoading] = useState(true); // 로딩 상태 추가

  const fetchData = useCallback(async () => {
    // 1. 캐시 데이터 확인
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data: cachedData, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        setData(cachedData);
        setLoading(false);
        return;
      }
    }

    // 2. 캐시 없거나 만료 시 API 호출
    try {
      setLoading(true);
      const portRequests = SORT_ORDER.map(s => axios.get(`${API_URL}/portfolios?limit=10&sortby=${SORT_MAP[s]}&direction=${(s==='mdd'||s==='volatility')?'asc':'desc'}&min_value=0&min_days=30`));
      const [postRes, ...portResponses] = await Promise.all([
        axios.get(`${API_URL}/posts?limit=20&sortBy=createdAt&direction=desc`), 
        ...portRequests
      ]);
      
      const portData = {};
      portResponses.forEach((res, i) => portData[SORT_ORDER[i]] = (res.data.portfolios || []).map(p => sanitizeData(p, ['nickname', 'name'])));
      
      const newData = {
        posts: (postRes.data.items || []).map(p => sanitizeData(p, ['title', 'previewTitle', 'authorNickname'])),
        portfolios: portData,
        sortIdx: 0,
        portIdx: 0
      };

      setData(newData);
      // 캐시 저장
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: newData, timestamp: Date.now() }));
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    fetchData(); 
    document.title = 'QuanPort-Home'; 
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setData(prev => ({
        ...prev,
        sortIdx: !hover.portfolio ? (prev.sortIdx + 1) % SORT_ORDER.length : prev.sortIdx,
      }));
    }, 4000);
    return () => clearInterval(interval);
  }, [hover.portfolio]);

  const getVisible = (list, currentIdx) => {
    if (!list || list.length === 0) return [];
    return Array.from({ length: Math.min(3, list.length) }, (_, i) => list[(currentIdx + i) % list.length]);
  };

  const currentCriteria = SORT_ORDER[data.sortIdx];
  const visiblePorts = data.portfolios[currentCriteria] ? getVisible(data.portfolios[currentCriteria], 0) : [];
  const visiblePosts = data.posts ? data.posts.slice(0, 3) : [];

  // 스켈레톤 컴포넌트
  const SkeletonCard = () => (
    <div className={`${styles.portfolioCard} ${styles.skeleton}`}>
      <div className={styles.skeletonCircle}></div>
      <div className={styles.skeletonLine} style={{ width: '60%' }}></div>
      <div className={styles.skeletonLine} style={{ width: '80%', marginTop: '20px' }}></div>
    </div>
  );

  return (
    <div className={commonStyles.Page}>
      {showAnnouncement && <AnnouncementPopup onClose={() => setShowAnnouncement(false)} />}

      {/* 1. HERO SECTION */}
      <div className={styles.heroWrapper}>
        <Link to="/backtest" className={styles.heroCard}>
          <RefreshCw size={48} color="#6a1b9a" />
          <h2 className={styles.heroTitle}>{t('navPage.hero.title')}</h2>
          <p className={styles.heroSubtitle}>{t('navPage.hero.subtitle')}</p>
          <div className={styles.heroAction}>
             {t('navPage.hero.button')} <ArrowRight size={18} />
          </div>
        </Link>
      </div>

      {/* 2. SHORTCUTS */}
      {/* <div className={styles.secondaryGrid}>
        <Link to="/portfolios" className={styles.featureCard}>
          <Trophy size={24} color="#f59e0b"/> 
          <span className={styles.label}>{t('navPage.quickActions.ranking')}</span>
        </Link>
        <Link to="/posts" className={styles.featureCard}>
          <BookOpen size={24} color="#3b82f6"/> 
          <span className={styles.label}>{t('navPage.quickActions.insight')}</span>
        </Link>
      </div> */}

    

      {/* 4. Latest Insights Section */}
      {/* <section className={styles.previewSection}>
        <h3 className={styles.sectionTitle}>{t('navPage.latestInsights.title')}<BookOpen size={24} /></h3>
        <div className={styles.latestPostsGrid}>
          {loading ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            visiblePosts.map(post => (
              <Link key={post.postId} to={`/posts/${post.postId}`} className={styles.postCard}>
                <h4 className={styles.postCardTitle}>{post.title}</h4>
                <p className={styles.postCardSnippet}>{post.previewTitle}</p>
                <div className={styles.postCardMeta}>
                  <img src={post.authorProfileImageUrl || DEFAULT_PROFILE} className={styles.profileImage} alt="a" />
                  <span>{post.authorNickname}</span>
                  <span className={styles.postCardDate}><Clock size={12}/> {new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
              </Link>
            ))
          )}
        </div>
      </section> */}

      {/* 5. EXPERT TOOLS */}
      <div className={styles.featureGrid}>
        <div className={styles.featureGroupWrapper}>
          <h4 className={styles.featureGroupTitle}>
            <Zap size={18} /> {t('navPage.expertTools.label')}
          </h4>
          <div className={styles.featureGroupItems}>
            {[
              { label: 'alloc', to: "/allocation", icon: <BarChart2 size={16}/> },
              { label: 'sentiment', to: "/sentiment", icon: <Zap size={16}/> }, 
              { label: 'risk', to: "/risk", icon: <AlertTriangle size={16}/> },
              { label: 'corr', to: "/correlation", icon: <List size={16}/> },
              { label: 'sim', to: "/simulation", icon: <Dice5 size={16}/> }
            ].map(item => (
              <Link key={item.label} to={item.to} className={styles.featureCardSmall}>
                {item.icon} <span className={styles.labelSmall}>{t(`navPage.quickActions.group.${item.label}`)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 2. SHORTCUTS */}
      <div className={styles.secondaryGrid}>
        <Link to="/portfolios" className={styles.featureCard}>
          <Trophy size={24} color="#f59e0b"/> 
          <span className={styles.label}>{t('navPage.quickActions.ranking')}</span>
        </Link>
        <Link to="/posts" className={styles.featureCard}>
          <BookOpen size={24} color="#3b82f6"/> 
          <span className={styles.label}>{t('navPage.quickActions.insight')}</span>
        </Link>
      </div>
      
        {/* 3. Top Portfolios Section */}
      {/* <section className={styles.previewSection} onMouseEnter={() => setHover(h => ({...h, portfolio: true}))} onMouseLeave={() => setHover(h => ({...h, portfolio: false}))}>
        <h3 className={styles.sectionTitle}>{t('navPage.topPortfolios.title')} <TrendingUp size={24} /></h3>
        <div className={styles.sortButtons}>
          {SORT_ORDER.map(c => (
            <button key={c} className={`${styles.sortButton} ${currentCriteria === c ? styles.active : ""}`} onClick={() => setData(d => ({...d, sortIdx: SORT_ORDER.indexOf(c)}))}>
              {t(`navPage.sort.${c}`)}
              {['sharpe', 'calmar'].includes(c) && <div className={styles.infoTooltipContainer}><Info size={16}/><div className={styles.tooltip}>{t(`navPage.${c}Info`)}</div></div>}
            </button>
          ))}
        </div>
        <div className={styles.topPortfoliosGrid}>
          {loading ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            visiblePorts.map(p => (
              <Link key={p.portfolioId} to={`/portfolios/${p.portfolioId}`} className={styles.portfolioCard}>
                <div className={styles.authorInfoContainer}>
                  <img src={p.profileImageUrl || DEFAULT_PROFILE} className={styles.profileImage} alt="p" />
                  <h4>{p.nickname}</h4>
                </div>
                <p className={styles.metricRow}>
                  <span className={styles.metricLabel}>{t(`navPage.metric.${currentCriteria}`)}: </span>
                  <span className={`${styles.metricValue} ${(p[SORT_MAP[currentCriteria]] > 0 && currentCriteria === 'return') ? styles.positiveMetric : ''}`}>
                    {['return', 'mdd'].includes(currentCriteria) ? `${(p[SORT_MAP[currentCriteria]] * 100).toFixed(2)}%` : p[SORT_MAP[currentCriteria]]?.toFixed(2)}
                  </span>
                </p>
                <p className={styles.portfolioCardDate}>
                  <Clock size={12}/> <span>{t('navPage.period.recent30Days')}</span>
                </p>
              </Link>
            ))
          )}
        </div>
      </section> */}



    </div>
  );
}

export default NavPage;